import React from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { CREATE_CONTACT } from "../../graph/contacts/mutations";
import useGraphQLClient from "../../hooks/useGraphQLClient";
import formOptions from "../../config/formOptions.json";
import CreateCompanyAddressModal from "../../components/CreateCompanyAddressModal.jsx";

const contactStatusValues =
  (formOptions?.contactStatusOptions || []).map((opt) => opt.value) || [];

const preferredCommsMethodValues =
  (formOptions?.preferredCommsMethodOptions || []).map((opt) => opt.value) || [];

const engagementLevelValues =
  (formOptions?.engagementLevelOptions || []).map((opt) => opt.value) || [];


const CreateContactForm = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();
  const [addressModalOpen, setAddressModalOpen] = React.useState(false);
  const [createdCompanyID, setCreatedCompanyID] = React.useState(null);


  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const input = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email || null,
        phone: values.phone || null,
        jobTitle: values.jobTitle || null,
        companyName: values.companyName || null,
        source: values.source || null,
        referredByName: values.referredByName || null,
        preferredCommsMethod: values.preferredCommsMethod || null,
        status: values.status || null,
        engagementLevel: values.engagementLevel || null,
        fitScore: values.fitScore === "" ? null : Number(values.fitScore),
        notes: values.notes || null,
      };

    const response = await client.request(CREATE_CONTACT, { input });

    console.log("CREATE_CONTACT response:", response);
    console.log("companyID:", response?.createContact?.companyID);

    console.log("createdContact full:", JSON.stringify(response.createContact, null, 2));
console.log("companyID:", response.createContact.companyID);
console.log("companyName:", response.createContact.companyName);

    const createdContact = response.createContact;

    setSnackbar({
      open: true,
      message: "Contact created successfully!",
      severity: "success",
    });

    if (createdContact.companyID) {
      setCreatedCompanyID(createdContact.companyID);
      setAddressModalOpen(true);
      return;
    }

    resetForm();
    navigate("/manageContacts");            
    } catch (err) {
      const message = err?.response?.errors?.[0]?.message || "Unexpected error";
      console.error("GraphQL Error:", err);
      console.error("GraphQL response errors:", err?.response?.errors);

      setSnackbar({
        open: true,
        message: `Failed to create contact: ${message}`,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Header title="CREATE CONTACT" subtitle="Create a New Contact" />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="First Name"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.firstName && !!errors.firstName}
                helperText={touched.firstName && errors.firstName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Last Name"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.lastName && !!errors.lastName}
                helperText={touched.lastName && errors.lastName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email ? errors.email : "Optional"}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Phone Number"
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone ? errors.phone : "Optional"}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Job Title"
                name="jobTitle"
                value={values.jobTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.jobTitle && !!errors.jobTitle}
                helperText={touched.jobTitle && errors.jobTitle ? errors.jobTitle : "Optional"}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Company Name"
                name="companyName"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.companyName && !!errors.companyName}
                helperText={
                  touched.companyName && errors.companyName ? errors.companyName : "Optional"
                }
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Source</InputLabel>
                <Select
                  name="source"
                  value={values.source}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.source && !!errors.source}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(formOptions.contactSourceOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                variant="filled"
                label="Referred By Name"
                name="referredByName"
                value={values.referredByName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.referredByName && !!errors.referredByName}
                helperText={
                  touched.referredByName && errors.referredByName
                    ? errors.referredByName
                    : "Optional"
                }
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.status && !!errors.status}
                >
                  {(formOptions.contactStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Preferred Contact Method</InputLabel>
                <Select
                  name="preferredCommsMethod"
                  value={values.preferredCommsMethod}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.preferredCommsMethod && !!errors.preferredCommsMethod}
                >
                  {(formOptions.preferredCommsMethodOptions || []).map((opt) => (
                    <MenuItem key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Engagement Level</InputLabel>
                <Select
                  name="engagementLevel"
                  value={values.engagementLevel}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.engagementLevel && !!errors.engagementLevel}
                >
                  {(formOptions.engagementLevelOptions || []).map((opt) => (
                    <MenuItem key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Fit Score</InputLabel>
                <Select
                  name="fitScore"
                  value={values.fitScore}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.fitScore && !!errors.fitScore}
                >
                  {(formOptions.fitScoreOptions || []).map((opt) => (
                    <MenuItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <MarkdownEditor
                value={values.notes}
                onChange={(nextValue) => setFieldValue("notes", nextValue)}
                label="Notes"
                minRows={14}
                error={!!touched.notes && !!errors.notes}
                helperText={touched.notes && errors.notes}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <ActionButton type="submit" tone="success">
                Create Contact
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>

      <CreateCompanyAddressModal
  open={addressModalOpen}
  companyID={createdCompanyID}
  onClose={() => {
    setAddressModalOpen(false);
    navigate("/manageContacts");
  }}
  onCreated={() => {
    setAddressModalOpen(false);
    navigate("/manageContacts");
  }}
/>
    </Box>
  );
};

const validationSchema = yup.object().shape({
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  email: yup.string().email("Invalid email").nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  jobTitle: yup.string().nullable().notRequired(),
  companyName: yup.string().nullable().notRequired(),
  source: yup.string().nullable().notRequired(),
  referredByName: yup.string().nullable().notRequired(),
  status: yup
    .string()
    .oneOf(contactStatusValues, "Invalid status")
    .required("Required"),
  preferredCommsMethod: yup
    .string()
    .oneOf(preferredCommsMethodValues, "Invalid preferred contact method")
    .nullable()
    .notRequired(),
  engagementLevel: yup
    .string()
    .oneOf(engagementLevelValues, "Invalid engagement level")
    .nullable()
    .notRequired(),
  fitScore: yup
    .mixed()
    .test("fitScore", "Fit Score must be between 1 and 5", (value) => {
      if (value === "" || value === null || value === undefined) return true;
      const num = Number(value);
      return Number.isInteger(num) && num >= 1 && num <= 5;
    }),
  notes: yup.string().nullable().notRequired(),
});

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
  companyName: "",
  source: "",
  referredByName: "",
  preferredCommsMethod: "",
  status: "TARGETED",
  engagementLevel: "",
  fitScore: "",
  notes: "",
};

export default CreateContactForm;